import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Add, CheckCircle, FileUpload, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";

import BaseMap from "fond/map/BaseMap";
import BaseMapButtons from "fond/map/BaseMapButtons";
import BaseMapStyleToggle from "fond/map/BaseMapStyleToggle";
import { MapContext } from "fond/map/MapProvider";
import { BBox } from "fond/types/geometry";
import { AccordionStepper, AccordionStepperPane, Dropzone, NonIdealState } from "fond/widgets";

import GCPSidebar from "./steps/GCPSidebar";
import ImageNameDialog from "./ImageNameDialog";
import ImageViewer from "./ImageViewer";
import MapGCPHandler from "./MapGCPHandler";
import MapImageLayer from "./MapImageLayer";
import { useGeoReferencer } from "./useGeoReferencer";

interface GeoReferencerWizardProps {
  bbox?: BBox | null;
  onClose: () => void;
  onConfirm: (data: {
    imageName: string;
    imageUrl: string;
    imageCorners: [[number, number], [number, number], [number, number], [number, number]];
    opacity: number;
  }) => void;
}

const GeoReferencerWizard: React.FC<GeoReferencerWizardProps> = ({ bbox, onClose, onConfirm }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { map, setMapStyle } = useContext(MapContext);
  const {
    imageName,
    imageUrl,
    imageCorners,
    opacity,
    isVisible,
    completeGcpCount,
    transformMatrix,
    residualError,
    mode,
    loadImage,
    addGcp,
    setOpacity,
    setVisible,
  } = useGeoReferencer();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        loadImage(files[0]);
      }
    },
    [loadImage]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        loadImage(files[0]);
      }
      // Reset so the same file can be re-selected
      e.target.value = "";
    },
    [loadImage]
  );

  // Trigger map resize when step changes (container width may change)
  useEffect(() => {
    // Allow the DOM to reflow before resizing
    const frame = requestAnimationFrame(() => {
      map?.resize();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeStep, map]);

  // Advance to GCP step when a new image is loaded
  const prevImageUrl = useRef(imageUrl);
  useEffect(() => {
    if (imageUrl && imageUrl !== prevImageUrl.current) {
      setActiveStep(1);
    }
    prevImageUrl.current = imageUrl;
  }, [imageUrl]);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, 2));
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const [showImageNameDialog, setShowImageNameDialog] = useState(false);

  const handleApplyClick = useCallback(() => {
    setShowImageNameDialog(true);
  }, []);

  const handleImageNameSubmit = useCallback(
    (name: string) => {
      if (imageUrl && imageCorners) {
        onConfirm({ imageName: name, imageUrl, imageCorners, opacity });
        setShowImageNameDialog(false);
        onClose();
      }
    },
    [imageUrl, imageCorners, opacity, onConfirm, onClose]
  );

  const handleOpacityChange = useCallback(
    (_: Event, value: number | number[]) => {
      setOpacity((value as number) / 100);
    },
    [setOpacity]
  );

  const canAdvanceToConfirm = completeGcpCount >= 3 && transformMatrix !== null;

  const panes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Upload Image",
        id: "upload",
        isComplete: imageUrl !== null,
        content: (
          <Dropzone onDropAccepted={handleDrop} acceptedFiles={{ "image/*": [".png", ".jpg", ".jpeg", ".bmp", ".webp"] }} multiple={false}>
            <NonIdealState
              icon={<FileUpload />}
              title="Upload Image"
              description="Drop an image here or click to browse. Supported formats: PNG, JPEG, BMP, WebP."
              size="small"
            />
          </Dropzone>
        ),
      },
      {
        header: "Define Ground Control Points",
        id: "gcps",
        isComplete: canAdvanceToConfirm,
        content: <GCPSidebar />,
      },
      {
        header: "Confirm",
        id: "confirm",
        isComplete: false,
        content: (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Review the georeferenced image on the map. Adjust opacity as needed, then click Apply to confirm.
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {`${completeGcpCount} GCPs${residualError != null ? ` | RMSE: ${residualError.toFixed(8)}` : ""}`}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={isVisible ? "Hide image" : "Show image"}>
                <IconButton size="small" onClick={() => setVisible(!isVisible)}>
                  {isVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 48 }}>
                Opacity
              </Typography>
              <Slider
                size="small"
                value={opacity * 100}
                onChange={handleOpacityChange}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        ),
      },
    ],
    [imageUrl, canAdvanceToConfirm, completeGcpCount, residualError, isVisible, opacity, handleDrop, handleOpacityChange, setVisible]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Main content row */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left sidebar */}
        <Box
          sx={{
            width: 450,
            minWidth: 450,
            display: "flex",
            flexDirection: "column",
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "auto",
          }}
        >
          <AccordionStepper
            id="georeferencer"
            panes={panes}
            autoMove={false}
            allowCollapse={false}
            activeIndex={activeStep}
            onActiveIndexChange={(index) => setActiveStep(index ?? 0)}
          />
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            position: "relative",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {/* Step 0: no image - explainer content */}
          {activeStep === 0 && !imageUrl && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                p: 4,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Georeferencing
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={2} textAlign="center">
                Georeferencing aligns a raster image (such as a scanned map, aerial photo, or design plan) to real-world map coordinates so it can be
                displayed as an overlay.
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1} textAlign="center">
                <strong>How it works:</strong>
              </Typography>
              <Box component="ol" sx={{ color: "text.secondary", typography: "body2", pl: 2, mb: 3 }}>
                <li>Upload an image file (PNG, JPEG, BMP, or WebP).</li>
                <li>Place at least 3 Ground Control Points (GCPs) by clicking matching locations on both the image and the map.</li>
                <li>Review the result and adjust opacity before applying.</li>
              </Box>
              <input ref={fileInputRef} type="file" hidden accept="image/png,image/jpeg,image/bmp,image/webp" onChange={handleFileInputChange} />
              <Button variant="contained" startIcon={<FileUpload />} onClick={() => fileInputRef.current?.click()}>
                Upload Image
              </Button>
            </Box>
          )}

          {/* Step 0: image already loaded - full preview */}
          {activeStep === 0 && imageUrl && (
            <Box sx={{ flex: 1, display: "flex" }}>
              <ImageViewer />
            </Box>
          )}

          {imageUrl && activeStep >= 1 && (
            <>
              {/* Add GCP overlay - shown on step 1 when idle */}
              {activeStep === 1 && mode === "idle" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 2,
                  }}
                >
                  <Button variant="contained" startIcon={<Add />} onClick={addGcp} size="small">
                    Add Ground Control Point
                  </Button>
                </Box>
              )}

              {/* ImageViewer - only on step 1 */}
              {activeStep === 1 && (
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", position: "relative" }}>
                  <ImageViewer />
                </Box>
              )}

              {/* Map */}
              <Box sx={{ flex: 1, minWidth: 0, position: "relative", "& .mapboxgl-ctrl-top-right": { display: "block !important" } }}>
                <BaseMap bbox={bbox ?? null} editMode="none" systemOfMeasurement="metric">
                  <MapGCPHandler />
                  {activeStep === 2 && <MapImageLayer />}
                  <Box className="left-sidebar-section">
                    <BaseMapStyleToggle onChange={setMapStyle} />
                  </Box>
                  <Box className="right-sidebar-section">
                    <div />
                    <BaseMapButtons
                      editMode="none"
                      onClickZoomIn={() => map?.zoomIn()}
                      onClickZoomOut={() => map?.zoomOut()}
                      currentLocationButton={false}
                    />
                  </Box>
                </BaseMap>
                {mode === "place-map" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "rgba(0,0,0,0.7)",
                      color: "white",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    Click on the map to place point
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Footer action buttons */}
      <Divider />
      <Box sx={{ px: 3, py: 1.5 }}>
        <Stack direction="row" justifyContent="space-between">
          <Button variant="text" onClick={activeStep === 0 ? onClose : handleBack}>
            {activeStep === 0 ? "Cancel" : "Back"}
          </Button>
          {activeStep === 1 && (
            <Button variant="contained" onClick={handleNext} disabled={!canAdvanceToConfirm}>
              Next
            </Button>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleApplyClick} startIcon={<CheckCircle />}>
              Apply
            </Button>
          )}
        </Stack>
      </Box>

      {showImageNameDialog && (
        <ImageNameDialog defaultName={imageName} onClose={() => setShowImageNameDialog(false)} onSubmit={handleImageNameSubmit} />
      )}
    </Box>
  );
};

export default GeoReferencerWizard;
